import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { ArrowRight, Loader2, Mail, User } from 'lucide-react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { InviteDetails, Identity } from '@/types'

type AcceptProps = {
  invite: InviteDetails
  identity: Identity | null
  needs_name: boolean
}

export default function InvitesAccept({ invite, identity }: AcceptProps) {
  const [verifying, setVerifying] = useState(false)
  const { data, setData, post, processing, errors } = useForm({
    name: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/invites/${invite.token}/accept`)
  }

  const handleVerifyEmail = () => {
    setVerifying(true)
    router.post(`/invites/${invite.token}/accept`, {}, {
      onFinish: () => setVerifying(false)
    })
  }

  if (!identity) {
    return (
      <AuthLayout
        title="Join Workspace"
        heading={`Join ${invite.account_name}`}
        description={`${invite.inviter_name} invited you to collaborate.`}
      >
        <div className="space-y-5">
          <div className="rounded-lg border border-border/60 p-4 space-y-3 bg-muted/30">
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-muted-foreground">Invited as</span>
              <Badge variant="outline" className="capitalize text-[11px] font-medium">{invite.role}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] text-muted-foreground">Email</span>
              <span className="text-[13px] font-medium">{invite.email}</span>
            </div>
          </div>

          <Button onClick={handleVerifyEmail} disabled={verifying} className="w-full h-11 text-sm font-semibold gap-2">
            {verifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending verification...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Verify with {invite.email}
              </>
            )}
          </Button>

          <p className="text-[11px] text-muted-foreground/60 text-center leading-relaxed">
            We'll send a code to verify your email before joining.
          </p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Join Workspace"
      heading={`Join ${invite.account_name}`}
      description="Add your name so your team knows who you are."
    >
      <div className="mb-5 flex items-center gap-2.5 py-2.5 px-3.5 rounded-lg bg-muted/60 border border-border/60">
        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-[11px] font-bold text-primary">
            {identity.email_address.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className="text-[13px] text-muted-foreground truncate">{identity.email_address}</span>
        <Badge variant="outline" className="capitalize text-[10px] font-medium ml-auto shrink-0">{invite.role}</Badge>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-[13px] font-medium text-foreground/70">Full name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
            <Input
              id="name"
              type="text"
              placeholder="Jane Doe"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              autoFocus
              required
              className="h-11 pl-10"
            />
          </div>
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name}</p>
          )}
          <p className="text-[11px] text-muted-foreground/60 pl-0.5">
            Visible to other members of this workspace.
          </p>
        </div>

        <Button type="submit" className="w-full h-11 text-sm font-semibold gap-2" disabled={processing}>
          {processing ? (
            <>
              <Loader2 className="animate-spin h-4 w-4" />
              Joining...
            </>
          ) : (
            <>
              Join workspace
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </form>
    </AuthLayout>
  )
}
